import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'

export interface AccessRequestPermissions {
    canView: boolean
    canEdit: boolean
    canDelete: boolean
    canSubmit: boolean
    canTest: boolean
    canApprove: boolean
}

/**
 * Permission gate for AccessRequestDetail (and the wizard in 607.09).
 *
 * The six write permissions are isolated as their own fields so callers can
 * gate each affordance independently — even where today's backend wires
 * multiple flags to the same role, future decoupling does not break call
 * sites. 607.09 extends the contract with `canCreate` if needed.
 *
 * Backend permission inventory for AccessRequests is narrower than for
 * AutoAssignRules: only `view`, `create`, `approve`, `cancel` exist. The
 * shape below maps the missing ones to the closest existing right per the
 * 603.05 contract surface — the page must still render every gate
 * independently so that 607.09 can extend without churn.
 *
 *  - {@code canView}    — read access to the detail page (`access-requests:view`)
 *  - {@code canEdit}    — currently aliased to `canSubmit` (no edit endpoint)
 *  - {@code canDelete}  — cancel-request maps here (`access-requests:cancel`)
 *  - {@code canSubmit}  — wizard submit (`access-requests:create`)
 *  - {@code canTest}    — n/a today, placeholder for future preview endpoint
 *  - {@code canApprove} — approve / reject items (`access-requests:approve`)
 */
export function useAccessRequestPermissions(): AccessRequestPermissions {
    const {hasPermission} = useAuth()
    const canSubmit = hasPermission(permissions.accessRequests.create)
    return {
        canView: hasPermission(permissions.accessRequests.view),
        canEdit: canSubmit,
        canDelete: hasPermission(permissions.accessRequests.cancel),
        canSubmit,
        canTest: false,
        canApprove: hasPermission(permissions.accessRequests.approve),
    }
}
